// ABOUTME: Truck Settings form — always shows trucks table with default truck dropdown.
// ABOUTME: UoM is fixed per country (first uomOption). defaultTruckSize is auto-derived from the default truck's capacity.
import {Button, ControlledInput, Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline, mdiPlus, mdiTrashCanOutline} from '@mdi/js'
import React, {useEffect, useMemo, useRef} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useRowTouchTracking} from '../utils'
import {toApiRequest} from '../hooks/useTruckSettingsMutations'
import {AvailableTrucksFormValues, TruckFormEntry, TruckSettingsConfig, TruckSettingsFormValues} from '../types/truckSettings'
import {FilterDropdown} from './FilterDropdown'
import {InlineCheckbox} from './InlineCheckbox'
import {NumericalInput} from './NumericalInput'
import {SectionTitle} from './SectionTitle'

interface TruckRowErrors {
  code?: string
}

export interface TruckSettingsFormProps {
  config: TruckSettingsConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: TruckSettingsFormValues) => void
  uomOptions?: {value: string; label: string}[]
  isRootScope?: boolean
  isTruckTypeSelectable?: boolean
  'data-cy'?: string
}

export const toFormValues = (cfg: TruckSettingsConfig): TruckSettingsFormValues => ({
  defaultTruckSize: cfg.defaultTruckSize,
  availableTrucks: cfg.availableTrucks,
})

export const TruckSettingsForm: React.FC<TruckSettingsFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  uomOptions = [],
  isRootScope = false,
  isTruckTypeSelectable = false,
  'data-cy': dataCy,
}) => {
  const {t} = useTranslation()
  const rowIdCounter = useRef(0)
  const nextRowIdForInit = () => String(rowIdCounter.current++)
  const formDefaultValues = useMemo(
    () => ({
      defaultTruckSize: config.defaultTruckSize,
      availableTrucks: config.availableTrucks
        ? {
            ...config.availableTrucks,
            trucks: config.availableTrucks.trucks.map((t) => ({...t, _rowId: nextRowIdForInit()})),
          }
        : {truckSizeUomId: uomOptions[0]?.value ?? '', defaultTruckCode: '', trucks: []},
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, uomOptions],
  )
  const {control, setValue, reset} = useForm<TruckSettingsFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const defaultTruckSize = useWatch({control, name: 'defaultTruckSize'})
  const availableTrucks = useWatch({control, name: 'availableTrucks'})

  const values: TruckSettingsFormValues = useMemo(
    () => ({defaultTruckSize, availableTrucks}),
    [defaultTruckSize, availableTrucks],
  )

  const hasChanges = useMemo(
    () => JSON.stringify(toApiRequest(values)) !== JSON.stringify(toApiRequest(formDefaultValues)),
    [values, formDefaultValues],
  )

  const trucksLength = availableTrucks?.trucks.length ?? 0
  const {touchedRows, markTouched, removeTouched, newRowRef: newRowCodeRef, nextRowId, markAdded} = useRowTouchTracking(trucksLength, config.availableTrucks?.trucks.length ?? 0)

  const isValid = useMemo(() => {
    const trucks = availableTrucks?.trucks ?? []
    if (trucks.length === 0) return false
    if (!availableTrucks?.truckSizeUomId) return false
    if (!trucks.every((t) => t.code.trim() !== '')) return false
    const codes = trucks.map((t) => t.code.trim().toLowerCase())
    if (new Set(codes).size !== codes.length) return false
    if (!trucks.every((t) => (t.truckSize ?? 0) > 0)) return false
    const defaultCode = availableTrucks?.defaultTruckCode ?? ''
    if (!defaultCode) return false
    if (!trucks.some((t) => t.code === defaultCode)) return false
    return true
  }, [availableTrucks])

  const truckErrors = useMemo<TruckRowErrors[]>(() => {
    const trucks = availableTrucks?.trucks ?? []
    const codes = trucks.map((t) => t.code.trim().toLowerCase())
    return trucks.map((truck, index) => {
      const errs: TruckRowErrors = {}
      const trimmed = truck.code.trim()
      if (!trimmed) {
        errs.code = t('countryConfigurations.truckSettings.validation.required')
      } else if (codes.indexOf(trimmed.toLowerCase()) !== index) {
        errs.code = t('countryConfigurations.truckSettings.validation.duplicateCode')
      }
      return errs
    })
  }, [availableTrucks?.trucks, t])

  const columnHeaders = useMemo(() => {
    const cols: {labelKey: string; tooltipKey?: string; className: string}[] = [
      {labelKey: 'countryConfigurations.truckSettings.columnCode', tooltipKey: 'countryConfigurations.truckSettings.columnCodeTooltip', className: 'w-[112px] shrink-0'},
      {labelKey: 'countryConfigurations.truckSettings.columnDescription', className: 'flex-1 min-w-0'},
      {labelKey: 'countryConfigurations.truckSettings.columnCapacity', className: 'w-[150px] shrink-0'},
    ]
    if (isTruckTypeSelectable) {
      cols.push({labelKey: 'countryConfigurations.truckSettings.columnOrderable', tooltipKey: 'countryConfigurations.truckSettings.columnOrderableTooltip', className: 'w-[100px] shrink-0'})
    }
    cols.push({labelKey: 'countryConfigurations.truckSettings.columnActions', className: 'w-[66px] shrink-0'})
    return cols
  }, [isTruckTypeSelectable])

  useEffect(() => {
    onDirtyChange(hasChanges)
  }, [hasChanges, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    if (hasChanges) onValuesChange?.(values)
  }, [values, hasChanges, onValuesChange])

  const handleDefaultTruckCodeChange = (val: string) => {
    setValue(
      'availableTrucks',
      {
        ...((availableTrucks ?? {truckSizeUomId: '', defaultTruckCode: '', trucks: []}) as AvailableTrucksFormValues),
        defaultTruckCode: val,
      },
      {shouldDirty: true},
    )
  }

  const selectedUomLabel = uomOptions[0]?.label ?? ''

  const defaultTruckOptions = useMemo(
    () =>
      (availableTrucks?.trucks ?? [])
        .filter((truck) => truck.code.trim() !== '')
        .map((truck) => ({value: truck.code, label: truck.code})),
    [availableTrucks?.trucks],
  )

  const handleAddTruck = () => {
    const current = availableTrucks ?? {truckSizeUomId: uomOptions[0]?.value ?? '', defaultTruckCode: '', trucks: []}
    const defaultTruck = current.trucks.find((truck) => truck.code === current.defaultTruckCode)
    markAdded()
    setValue('availableTrucks', {
      ...current,
      trucks: [{code: '', description: '', truckSize: defaultTruck?.truckSize ?? null, orderable: false, _rowId: nextRowId()}, ...current.trucks],
    }, {shouldDirty: true})
  }

  const handleRemoveTruck = (index: number) => {
    if (!availableTrucks) return
    const removedId = availableTrucks.trucks[index]?._rowId
    const newTrucks = availableTrucks.trucks.filter((_, i) => i !== index)
    const removedCode = availableTrucks.trucks[index]?.code
    const newValue = {
      ...availableTrucks,
      trucks: newTrucks,
      defaultTruckCode: availableTrucks.defaultTruckCode === removedCode ? (newTrucks[0]?.code ?? '') : availableTrucks.defaultTruckCode,
    }
    setValue('availableTrucks', newValue, {shouldDirty: true})
    if (removedId) removeTouched(removedId)
  }

  const handleTruckFieldChange = <K extends keyof TruckFormEntry>(index: number, field: K, value: TruckFormEntry[K]) => {
    if (!availableTrucks) return
    const newTrucks = availableTrucks.trucks.map((t, i) => i === index ? {...t, [field]: value} : t)
    const wasDefault = field === 'code' && availableTrucks.trucks[index]?.code === availableTrucks.defaultTruckCode
    const newDefaultTruckCode = wasDefault ? (value as string) : availableTrucks.defaultTruckCode
    setValue('availableTrucks', {...availableTrucks, trucks: newTrucks, defaultTruckCode: newDefaultTruckCode}, {shouldDirty: true})
  }

  return (
    <div className="p-6 flex flex-col gap-6" data-cy={dataCy}>
      {/* Default truck row */}
      <div className="flex items-start gap-8">
        {/* Label column */}
        <div className="flex flex-col gap-6 shrink-0">
          <div className="w-[200px] h-[40px] flex items-center gap-0.5">
            <TextMedium className="text-foreground">
              {t('countryConfigurations.truckSettings.defaultTruck')}
            </TextMedium>
            <CountryConfigsTooltip content={t('countryConfigurations.truckSettings.defaultTruckTooltip')} delayMs={300}>
              <span className="flex items-center cursor-default text-foreground">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
          </div>
        </div>
        {/* Value column */}
        <div className="flex flex-col gap-6 w-[258px]">
          <div className="flex gap-1 w-full h-[40px] items-center">
            <FilterDropdown
              value={availableTrucks?.defaultTruckCode ?? ''}
              options={defaultTruckOptions}
              size="regular"
              onChange={handleDefaultTruckCodeChange}
              disabled={isReadOnly || (availableTrucks?.trucks ?? []).length <= 1}
              placeholder={t('countryConfigurations.truckSettings.selectPlaceholder')}
            />
          </div>
        </div>
      </div>

      {/* Unit of measure row */}
      <div className="flex items-start gap-8">
        {/* Label column */}
        <div className="flex flex-col gap-6 shrink-0">
          <div className="w-[200px] h-[40px] flex items-center gap-0.5">
            <TextMedium className="text-foreground">
              {t('countryConfigurations.truckSettings.unitOfMeasure')}
            </TextMedium>
            <CountryConfigsTooltip content={t('countryConfigurations.truckSettings.unitOfMeasureTooltip')} delayMs={300}>
              <span className="flex items-center cursor-default text-foreground">
                <Icon path={mdiInformationOutline} size="regular" />
              </span>
            </CountryConfigsTooltip>
          </div>
        </div>
        {/* Value column */}
        <div className="flex flex-col gap-6 w-[258px]">
          <div className="flex gap-1 w-full h-[40px] items-center">
            <FilterDropdown
              value={availableTrucks?.truckSizeUomId ?? ''}
              options={uomOptions}
              size="regular"
              onChange={(val) => {
                if (!availableTrucks) return
                setValue('availableTrucks', {...availableTrucks, truckSizeUomId: val}, {shouldDirty: true})
              }}
              disabled={isReadOnly || uomOptions.length <= 1}
              placeholder={t('countryConfigurations.truckSettings.selectPlaceholder')}
              data-cy="truck-settings-uom"
            />
          </div>
        </div>
      </div>

      {/* Available trucks table */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionTitle titleKey="countryConfigurations.truckSettings.availableTruckTypes" />
          {config.isAvailableTrucksOverridden && !isRootScope && (
            <span className="text-info text-sm">
              {t('countryConfigurations.truckSettings.overriddenAtThisLevel')}
            </span>
          )}
        </div>

        {!isReadOnly && (
          <Button variant="secondary" size="small" iconPrefix={mdiPlus} onClick={handleAddTruck} className="w-full">
            {t('countryConfigurations.truckSettings.addTruck')}
          </Button>
        )}

        {(availableTrucks?.trucks ?? []).length > 0 && (
          <div className="w-full relative z-10">
            {/* Column headers */}
            <div className="flex bg-columnheader_background rounded-t-md">
              {columnHeaders.map((col) => (
                <div key={col.labelKey} className={`${col.className} px-2 py-2 flex items-center gap-1`}>
                  <TextMedium className="text-foreground_faded text-sm font-normal whitespace-nowrap">
                    {t(col.labelKey)}
                  </TextMedium>
                  {col.tooltipKey && (
                    <CountryConfigsTooltip content={t(col.tooltipKey)} delayMs={300}>
                      <span className="text-foreground_faded flex items-center cursor-default">
                        <Icon path={mdiInformationOutline} size="regular" />
                      </span>
                    </CountryConfigsTooltip>
                  )}
                </div>
              ))}
            </div>

            {/* Rows */}
            {(availableTrucks?.trucks ?? []).map((truck, index) => {
              const rowErrors = truckErrors[index] ?? {}
              return (
              <div key={truck._rowId ?? String(index)} className="flex border-b border-border" data-cy={`truck-row-${index}`}>
                {/* Code */}
                <div className="w-[130px] shrink-0 px-2 h-14 flex items-center">
                  <CountryConfigsTooltip content={touchedRows.has(truck._rowId ?? String(index)) ? (rowErrors.code ?? '') : ''} delayMs={300}>
                    <div className="w-full" ref={index === 0 ? newRowCodeRef : undefined}>
                          <ControlledInput
                            value={truck.code}
                            onChange={(e) => handleTruckFieldChange(index, 'code', e.target.value)}
                            onBlur={() => markTouched(truck._rowId ?? String(index))}
                            isDisabled={isReadOnly}
                            variant="small"
                            error={touchedRows.has(truck._rowId ?? String(index)) ? rowErrors.code : undefined}
                            isTouched={false}
                            data-cy={`truck-${index}-code`}
                            className="w-full"
                          />
                        </div>
                      </CountryConfigsTooltip>
                    </div>

                    {/* Description */}
                    <div className="flex-1 min-w-0 px-2 h-14 flex items-center">
                      <ControlledInput
                        value={truck.description}
                        onChange={(e) => handleTruckFieldChange(index, 'description', e.target.value)}
                        isDisabled={isReadOnly}
                        variant="small"
                        isTouched={false}
                        data-cy={`truck-${index}-description`}
                        className="w-full"
                      />
                    </div>

                    {/* Capacity */}
                    <div className="w-[180px] shrink-0 px-2 h-14 flex items-center">
                      <CountryConfigsTooltip
                        content={(truck.truckSize ?? 0) <= 0 ? t('countryConfigurations.truckSettings.validation.truckSizePositive') : ''}
                        delayMs={300}
                      >
                        <div>
                          <NumericalInput
                            value={truck.truckSize ?? 0}
                            size="small"
                            onChange={(v) => handleTruckFieldChange(index, 'truckSize', v)}
                            min={0.01}
                            disabled={isReadOnly}
                            suffix={selectedUomLabel}
                            hasError={(truck.truckSize ?? 0) <= 0}
                            data-cy={`truck-${index}-truckSize`}
                          />
                        </div>
                      </CountryConfigsTooltip>
                    </div>

                    {/* Orderable */}
                    {isTruckTypeSelectable && (
                    <div className="w-[80px] shrink-0 px-2 h-14 flex items-center justify-start">
                      <InlineCheckbox
                        checked={truck.orderable}
                        onChange={(checked) => handleTruckFieldChange(index, 'orderable', checked)}
                        disabled={isReadOnly}
                        label={t('countryConfigurations.truckSettings.columnOrderable')}
                        data-cy={`truck-${index}-orderable`}
                      />
                    </div>
                    )}

                    {/* Actions */}
                    <div className="w-[48px] shrink-0 px-2 h-14 flex items-center justify-center">
                      {!isReadOnly && (
                        trucksLength <= 1 ? (
                          <CountryConfigsTooltip
                            content={t('countryConfigurations.truckSettings.cannotDeleteDefaultTruck')}
                            delayMs={300}
                          >
                            <Button
                              variant="secondary"
                              square
                              outline
                              size="regular"
                              disabled
                              aria-label={`Delete truck ${index + 1}`}
                              data-cy={`truck-${index}-delete`}
                            >
                              <Icon path={mdiTrashCanOutline} size="regular" />
                            </Button>
                          </CountryConfigsTooltip>
                        ) : (
                          <Button
                            variant="secondary"
                            square
                            outline
                            size="regular"
                            onClick={() => handleRemoveTruck(index)}
                            aria-label={`Delete truck ${index + 1}`}
                            data-cy={`truck-${index}-delete`}
                          >
                            <Icon path={mdiTrashCanOutline} size="regular" />
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                )})}
              </div>
            )}
          </div>
    </div>
  )
}
